import React, { ReactNode, useState, useEffect } from "react";
import Header from "@cloudscape-design/components/header";
import { ListTenant } from "../data/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import title from "process";
import { Link } from "react-router-dom";

interface CellProps {
  StatusPayment: boolean;
}

const PaymentStatus: React.FC<CellProps> = ({ StatusPayment }) => {
  let statusType: "success" | "error" = "error"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (StatusPayment === true) {
    statusType = "success";
  } else if (StatusPayment === false) {
    statusType = "error";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Paid"
        : statusType === "error"
        ? "Unpaid"
        : "Unknown Status"}
    </StatusIndicator>
  );
};

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  ListTenant: ListTenant[];
}

const columnDefinitions: TableProps<ListTenant>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    width: 10,
  },
  {
    header: "Tenant Name",
    cell: ({ TenantName }) => (
      <Link to={`/system/tenants/${TenantName}`}>{TenantName}</Link>
    ),
    sortingField: "Name",
    width: 100,
  },
  {
    header: "Subtenant",
    cell: ({ Subtenant }) => Subtenant,
    sortingField: "Subtenant",
    width: 100,
  },
  {
    header: "Address",
    cell: ({ Address }) => Address,
    sortingField: "Name",
    maxWidth: 100,
  },
  {
    header: "Contact",
    cell: ({ Contact }) => Contact,
    sortingField: "Contact",
    maxWidth: 150,
  },
  {
    header: "Phone",
    cell: ({ Phone }) => Phone,
    sortingField: "Position",
    maxWidth: 100,
  },
  {
    header: "Status",
    cell: ({ Status }) => Status,
    sortingField: "Status",
    maxWidth: 100,
  },
  {
    header: "Service Package",
    cell: ({ ServicePacakge }) => ServicePacakge,
    sortingField: "ServicePackage",
    maxWidth: 100,
  },
  {
    header: "Status Payment",
    cell: PaymentStatus,
    sortingField: "StatusPayment",
    maxWidth: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly ListTenant[] = [],
  selectedItems: readonly ListTenant[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VariationTable({ ListTenant }: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 20 });
  const [visible, setVisible] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<ListTenant>(ListTenant, {
    filtering: {
      filteringFunction: (item, filteringText) => {
        const filterTextLower = filteringText.toLowerCase();
        const statusText = item.StatusPayment ? "paid" : "unpaid";
        if (statusText === filterTextLower) {
          return true;
        }
        return (
          item.id.toLocaleLowerCase().includes(filterTextLower) ||
          item.TenantName.toLowerCase().includes(filterTextLower) ||
          item.Subtenant.toLowerCase().includes(filterTextLower) ||
          item.Address.toLowerCase().includes(filterTextLower) ||
          item.Contact.toLowerCase().includes(filterTextLower) ||
          item.Phone.toLowerCase().includes(filterTextLower) ||
          item.Status.toLowerCase().includes(filterTextLower) ||
          item.ServicePacakge.toLowerCase().includes(filterTextLower)
        );
      },

      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      <Table<ListTenant>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Vehicle table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              ListTenant,
              collectionProps.selectedItems,
              items.length
            )}
          >
            Tenants
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Tenant'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confirm'
            cancelLabel='Cancel'
          />
        }
        //selectionType='single'
      />
    </>
  );
}
